import React, { useState, useEffect } from 'react';
import { Button, Card, Row, Col, Alert } from 'reactstrap';
import { Activity, Loader, CheckCircle, XCircle, Heart, UserPlus } from 'lucide-react';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const BloodPressureAnnotation = ({ 
  onSkip, 
  onContinue, 
  selectedSubject, 
  annotationMessages,
  setAnnotationMessages ,
  recordingId
}) => {
  const [showInitialPrompt, setShowInitialPrompt] = useState(true);
  const [status, setStatus] = useState('initial'); // initial, waiting, measuring, completed
  const [error, setError] = useState(null);
  const [annotators, setAnnotators] = useState([]);


  useEffect(() => {
    if (!showInitialPrompt && status !== 'completed') {
      // Handle annotator messages
      Object.values(annotationMessages).forEach(message => {
        if (message.type === 'ANNOTATING_ADDED') {
          setAnnotators(prev => {
            // Create a new annotator object
            const newAnnotator = {
              annotatorId: message.data.annotatorId,
              measurement: null
            };

            // Check if this annotator already exists
            const existingIndex = prev.findIndex(a => a.annotatorId === message.data.annotatorId);
            
            if (existingIndex !== -1) {
              // Replace existing annotator
              const newAnnotators = [...prev];
              newAnnotators[existingIndex] = newAnnotator;
              return newAnnotators;
            } else if (prev.length < 2) {
              // Add new annotator if we have less than 2
              return [...prev, newAnnotator];
            }
            
            return prev; // Don't add if we already have 2 annotators
          });
        } else if (message.type === 'ANNOTATING_BP') {
          setAnnotators(prev => {
            return prev.map(annotator => 
              annotator.annotatorId === message.data.annotatorId 
                ? { ...annotator, measurement: message.data }
                : annotator
            );
          });
        } else if (message.type === 'SESSION_LEFT') {
          setAnnotators(prev => 
            prev.filter(annotator => annotator.annotatorId !== message.data.stationId)
          );

        } else if (message.type === 'ANNOTATING_DONE') {
          setStatus('completed');
        }
      });
    }
  }, [annotationMessages, showInitialPrompt]);

  const startAnnotating = async () => {
    try {
      setStatus('waiting');
      setError(null);
      await axios.post(`${BASE_URL}web/startAnnotating`, {
        patientId: selectedSubject.subjectId,
        recordingId: recordingId, 
      });
    } catch (err) {
      setError('Failed to start blood pressure measurement');
      setStatus('initial');
    }
  };

  const renderAnnotatorSlot = (index) => {
    const annotator = annotators[index];
    const measurement = annotator?.measurement;

    return (
      <Card className="border-0 shadow-sm bg-white overflow-hidden">
        <div className="p-4">
          <div className="text-center position-relative" style={{ minHeight: '140px' }}>
            {!annotator ? (
              <div className="d-flex flex-column align-items-center justify-content-center h-100">
                <div className="bg-light rounded-circle p-3 mb-2">
                  <UserPlus size={32} className="text-primary" />
                </div>
                <p className="text-muted mb-0">Waiting for annotator...</p>
              </div>
            ) : !measurement ? (
              <div className="d-flex flex-column align-items-center justify-content-center h-100">
                <div className="bg-light rounded-circle p-3 mb-2 position-relative">
                  <Heart size={32} className="text-primary" />
                  <div className="position-absolute" style={{ 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)',
                    animation: 'pulse 2s infinite'
                  }}>
                    <Heart size={40} className="text-primary" style={{ opacity: 0.2 }} />
                  </div>
                </div>
                <p className="text-muted mb-0">Taking measurement...</p>
              </div>
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center h-100">
                <div className="position-relative">
                  <div className="display-4 font-weight-bold text-primary mb-0" style={{
                    background: 'linear-gradient(45deg, #4F46E5, #7C3AED)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    {measurement.systolic}/{measurement.diastolic}
                  </div>
                  <div className="text-muted">mmHg</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
    );
  };


  const renderResult = () => {
    const resultMessage = Object.values(annotationMessages)
      .find(msg => msg.type === "ANNOTATING_DONE");
    
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: '2rem'
      }}>
        {resultMessage?.data.accepted ? (
          <>
            <CheckCircle size={80} style={{ color: '#10B981', marginBottom: '1rem' }} />
            <h3 style={{ color: '#94A3B8', marginBottom: '1.5rem' }}>Measurement Accepted</h3>
          </>
        ) : (
          <>
            <XCircle size={80} style={{ color: '#EF4444', marginBottom: '1rem' }} />
            <h3 style={{ color: '#94A3B8', marginBottom: '1.5rem' }}>Measurement Rejected</h3>
          </>
        )}

        <div style={{
          width: '100%',
          maxWidth: '400px',
          marginTop: '1rem',
          padding: '1.5rem',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '12px'
        }}>
          <h4 style={{ 
            color: '#94A3B8', 
            textAlign: 'center', 
            marginBottom: '1rem',
            fontSize: '1.1rem' 
          }}>
            Annotator Measurements
          </h4>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-around',
            gap: '1rem'
          }}>
            {annotators.map((annotator, index) => (
              <div 
                key={annotator.annotatorId}
                style={{
                  textAlign: 'center',
                  padding: '1rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '8px',
                  flex: 1
                }}
              >
                <div style={{ color: '#94A3B8', marginBottom: '0.5rem' }}>
                  Annotator {index + 1}
                </div>
                <div style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  background: 'linear-gradient(45deg, #60A5FA, #A78BFA)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  {annotator.measurement.systolic}/{annotator.measurement.diastolic}
                </div>
                <div style={{ color: '#94A3B8', marginTop: '0.25rem' }}>
                  mmHg
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="d-flex gap-3 justify-content-center mt-4">
          <Button 
            color="secondary" 
            size="lg"
            onClick={() => {
              setStatus('initial');
              setAnnotationMessages({});
              setAnnotators([]);
              startAnnotating();
            }}
          >
            Take Another Measurement
          </Button>
          <Button color="primary" size="lg" onClick={onContinue}>
            Continue to Recording
          </Button>
        </div>
      </div>
    );
  };
  if (showInitialPrompt) {
    return (
      <div className="text-center py-5">
        <div className="mb-4">
          <div className="d-inline-flex justify-content-center align-items-center bg-primary bg-opacity-10 rounded-circle p-3">
            <Activity size={40} className="text-primary" />
          </div>
        </div>
        <h4 className="mb-3">Blood Pressure Measurement</h4>
        <p className="text-muted mb-4">Would you like to start a blood pressure measurement session?</p>
        <div className="d-flex gap-3 justify-content-center">
          <Button color="primary" size="lg" onClick={() => {
            setShowInitialPrompt(false);
            startAnnotating();
          }}>
            Yes, start measurement
          </Button>
          <Button color="secondary" size="lg" onClick={onSkip}>
            No, skip for now
          </Button>
        </div>
      </div>
    );
  }


  return (
    <div className="p-4">
      {error && (
        <Alert color="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {status === 'completed' ? (
        renderResult()
      ) : (
        <div>
          <div className="text-center mb-4">
            <h4 className="mb-1">Blood Pressure Measurement</h4>
            <p className="text-muted">Measurements from two annotators are required</p>
          </div>
          <Row className="g-4 justify-content-center">
            <Col xs={12} md={6}>
              {renderAnnotatorSlot(0)}
            </Col>
            <Col xs={12} md={6}>
              {renderAnnotatorSlot(1)}
            </Col>
          </Row>
          {status === 'waiting' && (
            <div className="text-center mt-4">
              <div className="d-inline-flex align-items-center gap-2 text-muted">
                <Loader size={16} className="animate-spin" />
                <span>
                  {annotators.length === 2 
                    ? 'Waiting for measurements...' 
                    : 'Waiting for annotators to join...'}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      <style>
        {`
          @keyframes pulse {
            0% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 0.2;
            }
            50% {
              transform: translate(-50%, -50%) scale(1.3);
              opacity: 0.1;
            }
            100% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 0.2;
            }
          }
        `}
      </style>
    </div>
  );
};

export default BloodPressureAnnotation;